/* eslint-disable no-param-reassign */
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectOption } from '../../../../types';
import {
  type Value,
  type ModifierConfig,
  type ModifierGroup,
  type ColumnHandler,
  GroupOptions,
  optionNamespace,
  TypePeriodOptions,
} from '../../../../hooks/modifiers';
import Select from '../../../../juristec-ui/core/SelectNew';
import SelectTips from '../../../Popovers/SelectTips';
import KpiFilter from '../../../KpiFilter';
import Button from '../../../../juristec-ui/core/Button';
import Tooltip from '../../../../juristec-ui/core/Tooltip';
import InputTextLine from '../../../../juristec-ui/core/InputTextLine';
import { trimString } from '../../../../juristec-ui/utils/functions/formatString';
import { verifyInput } from '../../../../juristec-ui/utils/validators/inputTextValidators';
import { dateOptions } from '../../../../options';

import {
  GroupColumn, GroupRow, GroupContainer, BlockWrapper, MappingContainer, GroupAdvice,
} from './styled/MapStep.styled';

interface MapStepProps {
  modifiers: ModifierGroup;
  hasMap: boolean;
  columnNameState: [
    { value: string, error: boolean, errorMsg: string }, (value: string) => void
  ];
  columnOpts?: SelectOption[];
  modifierConfig: ModifierConfig;
  simpleOptions: SelectOption[];
  getColumnOptions: (columns: ColumnHandler) => SelectOption[];
  getSimpleOptions: (modifiers: ModifierGroup) => SelectOption[];
  handleModifierConfigs: (newData: Partial<ModifierConfig>) => void;
  configColumnIsDate: () => boolean;
  getOptionsByConfigGroup: () => SelectOption[];
  checkModifierValues: () => boolean;
  addNewGroup: () => void;
  editMode: boolean;
  children: ReactNode;
}

interface GroupNameState {
  value: string;
  error: boolean;
  errorMsg: string;
}

const defaultProps = {
  columnOpts: [],
  addedGroups: [],
};

const MapStep = ({
  modifiers,
  hasMap,
  columnNameState,
  columnOpts = defaultProps.columnOpts,
  modifierConfig,
  simpleOptions,
  getColumnOptions,
  getSimpleOptions,
  handleModifierConfigs,
  configColumnIsDate,
  getOptionsByConfigGroup,
  checkModifierValues,
  addNewGroup,
  editMode,
  children,
}: MapStepProps) => {
  const { t } = useTranslation();

  const [columnName, handleColumnName] = columnNameState;
  const [groupName, setGroupName] = useState<GroupNameState>({ value: '', error: true, errorMsg: '' });

  const handleColumnSelect = (
    selected: SelectOption,
  ) => handleModifierConfigs({ column: selected });

  const handleGroupSelect = (
    selected: SelectOption,
  ) => handleModifierConfigs({ group: selected });

  const handleSelectorSelect = (
    selected: SelectOption,
  ) => handleModifierConfigs({ selector: selected });

  const handleFormatSelect = (
    selected: SelectOption,
  ) => handleModifierConfigs({ format: selected });

  const handleGroupChange = (values: Value[]) => handleModifierConfigs({ values });

  const handleGroupName = (value: string) => {
    const msg = verifyInput(value, true);
    setGroupName({
      value,
      error: msg.length !== 0,
      errorMsg: msg,
    });
    if (msg.length === 0) {
      handleModifierConfigs({ rule: value });
    }
  };

  const handleAddNewGroup = () => {
    addNewGroup();
    setGroupName({ value: '', error: true, errorMsg: '' });
  };

  return (
    <GroupContainer>
      <GroupAdvice>
        <b>Obs:</b>
        {' '}
        A lógica do depara apenas é aplicada quando o arquivo é atualizado.
      </GroupAdvice>
      <GroupColumn>
        <GroupRow
          className="cc-name"
          as={Tooltip}
          text="Não é permitido editar o nome de uma coluna já existente"
          textWhen={editMode}
          atModal
          direction="top"
        >
          <InputTextLine
            label="Nome da nova coluna"
            value={columnName.value}
            errorMessage={columnName.errorMsg}
            error={columnName.errorMsg.length > 0}
            onChange={(e) => handleColumnName(e.target.value)}
            setValue={() => {}}
            onBlur={() => handleColumnName(trimString(columnName.value))}
            readOnly={editMode}
          />
        </GroupRow>
        <MappingContainer>
          <GroupRow>
            <BlockWrapper
              as={Tooltip}
              text="Não é permitido alterar a coluna quando um depara foi selecionado"
              textWhen={hasMap}
              atModal
              direction="top"
            >
              <Select
                label="Depara por"
                onChange={handleColumnSelect}
                // @ts-expect-error not typed yet
                value={modifierConfig.column}
                options={getColumnOptions({ group: columnOpts })}
                atModal
                searchable
                sortBy="ascending"
              />
            </BlockWrapper>
            <div>
              <Select
                label="Tipo"
                placeholder={undefined}
                onChange={handleGroupSelect}
                // @ts-expect-error not typed yet
                value={modifierConfig.group}
                options={GroupOptions.list}
                optionNamespace={optionNamespace}
                atModal
              />
            </div>
          </GroupRow>
          {(modifierConfig.column?.value?.length ?? 0) > 0 && (
            <GroupRow>
              {modifierConfig.group.value === GroupOptions.enum.SIMPLE
                && configColumnIsDate() && (
                <div>
                  <Select
                    label="Formato"
                    onChange={handleSelectorSelect}
                    // @ts-expect-error not typed yet
                    value={modifierConfig.selector}
                    options={dateOptions}
                    searchable
                    atModal
                  />
                </div>
              )}
              <div style={{ position: 'relative' }}>
                <Select
                  label="Condição"
                  onChange={handleFormatSelect}
                  // @ts-expect-error not typed yet
                  value={modifierConfig.format}
                  options={getOptionsByConfigGroup()}
                  optionNamespace={optionNamespace}
                  searchable
                  atModal
                />
                {modifierConfig.format.value !== '' && (
                  <SelectTips
                    // @ts-expect-error not typed yet
                    description={t(modifierConfig.format.description, {
                      ns: optionNamespace,
                      ...(modifierConfig.format.params ?? {}),
                    })}
                    style={{ top: '5px', right: '5px' }}
                  />
                )}
              </div>
            </GroupRow>
          )}
          {(modifierConfig.column?.value?.length ?? 0) > 0
            && modifierConfig.format?.value.length > 0
            && (
              modifierConfig.group?.value === GroupOptions.enum.ADVANCED || simpleOptions.length > 0
            ) && (
            <>
              {!Object.keys(TypePeriodOptions.map).includes(modifierConfig.format.value) && (
                <KpiFilter
                  filterType={modifierConfig.format.value}
                  // @ts-expect-error not typed yet
                  handle={handleGroupChange}
                  // @ts-expect-error not typed yet
                  values={modifierConfig.values}
                  // @ts-expect-error not typed yet
                  options={getSimpleOptions(modifiers)}
                  format={modifierConfig.column?.type}
                  atModal
                />
              )}
              <InputTextLine
                label="Texto de associação"
                type="text"
                style={{ marginBottom: 0 }}
                onChange={(e) => handleGroupName(e.target.value)}
                value={groupName.value}
                errorMessage={groupName.errorMsg}
                error={groupName.errorMsg.length > 0}
                styleContainer={{ marginTop: '-8px' }}
                onBlur={(e) => handleGroupName(trimString(e.target.value))}
                setValue={() => {}}
              />
              <Button
                onClick={handleAddNewGroup}
                color="secondary"
                disabled={!checkModifierValues() || groupName.error}
                style={{ margin: '15px 0 0 0', width: '100%' }}
              >
                Adicionar depara
              </Button>
            </>
          )}
        </MappingContainer>
      </GroupColumn>
      <GroupColumn>
        {children}
      </GroupColumn>
    </GroupContainer>
  );
};

export default MapStep;
